import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../../../components/layout";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import { deleteFreightOrder, getFreightOrderIndex } from "../../../utils/api";
import { parseFreightIndexResponse } from "../../../utils/freightIndexResponse";
import { showError, showSuccess } from "../../../utils/toast";
import { FREIGHT_ORDER_COLUMNS } from "../../../config/freightAdminColumns";
import { useFreightAdminTablePrefs } from "../../../hooks/useFreightAdminTablePrefs";
import FreightAdminTableToolbar from "../../../components/admin/FreightAdminTableToolbar";
import FreightOrderDtActions from "../../../components/admin/FreightOrderDtActions";
import { AdminServerList, DataTablePageSize } from "../../../components/datatable";
import { AutoRefreshToggle, readAutoRefreshEnabledFromStorage } from "../../../components/common";
import { FREIGHT_DT_LENGTH_MENU_25 } from "../../../components/admin/freightServerSideDtDefaults";
import "./adminFreight.css";

const TABLE_ID = "freight_orders";
const COLUMN_DEFS = FREIGHT_ORDER_COLUMNS;
const AUTO_REFRESH_STORAGE_KEY = "freight_orders_auto_refresh";

const FreightOrdersPage = () => {
  const navigate = useNavigate();
  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = useMemo(
    () => [
      { label: roleLabel, link: "/dashboard" },
      { label: "Freight", link: "/freight" },
      { label: "Orders" }
    ],
    [roleLabel]
  );

  const columnDefs = useMemo(() => COLUMN_DEFS, []);
  const lengthMenu = useMemo(() => Array.from(FREIGHT_DT_LENGTH_MENU_25[0]), []);
  const [pageSize, setPageSize] = useState(() => FREIGHT_DT_LENGTH_MENU_25[0][0]);

  const {
    visibility,
    toggleColumn,
    resetColumnsVisible,
    resetColumnOrder,
    orderedColumnDefs,
    onColumnReorder
  } = useFreightAdminTablePrefs(TABLE_ID, columnDefs);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [recordsFiltered, setRecordsFiltered] = useState(0);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(() =>
    readAutoRefreshEnabledFromStorage(AUTO_REFRESH_STORAGE_KEY)
  );
  const tableRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const getNavigate = useCallback(() => navigate, [navigate]);

  const handleCounts = useCallback((filtered, total) => {
    setRecordsFiltered(typeof filtered === "number" && filtered >= 0 ? filtered : 0);
    setRecordsTotal(typeof total === "number" && total >= 0 ? total : 0);
  }, []);

  const fetchPage = useCallback(
    async ({ page, pageSize: ps, search: q, sortBy, sortDir }) => {
      const res = await getFreightOrderIndex({
        page,
        per_page: ps,
        search: q,
        sort_by: sortBy,
        sort_dir: sortDir
      });
      const parsed = parseFreightIndexResponse(res, ps);
      return {
        list: parsed.list,
        recordsTotal: parsed.recordsTotal,
        recordsFiltered: parsed.recordsFiltered
      };
    },
    []
  );

  const handleDelete = useCallback(
    async (row) => {
      if (!row?.id) return;
      const ok = window.confirm(
        `Delete freight order ${row.auto_id || row.id}? This cannot be undone from the grid.`
      );
      if (!ok) return;
      try {
        await deleteFreightOrder({ id: row.id });
        showSuccess("Order deleted");
        tableRef.current?.reload?.();
      } catch (e) {
        showError(e.message || "Delete failed");
      }
    },
    []
  );

  const renderActions = useCallback(
    (row) => <FreightOrderDtActions row={row} onDelete={handleDelete} getNavigate={getNavigate} />,
    [handleDelete, getNavigate]
  );

  const getRowClassName = useCallback((row) => {
    return String(row?.order_status) === "1" ? "admin-dt-row--order-open" : "";
  }, []);

  const refreshTable = useCallback(() => {
    tableRef.current?.reload?.();
  }, []);

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="max-w-[1400px]">
        <FreightAdminTableToolbar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search (server-side)…"
          columnDefs={orderedColumnDefs}
          visibility={visibility}
          onToggleColumn={toggleColumn}
          onResetColumns={resetColumnsVisible}
          onResetColumnOrder={resetColumnOrder}
          rightSlot={
            <Link
              to="/freight/orders/new"
              className="btn-primary"
              style={{ textDecoration: "none", display: "inline-block" }}
            >
              + New order
            </Link>
          }
        />

        <div className="card offers-table border-l-4 border-l-[#064E8D]">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 min-w-0">
              <h3 className="m-0">
                Freight orders ({recordsFiltered}
                {recordsTotal !== recordsFiltered ? ` of ${recordsTotal}` : ""})
              </h3>
              <AutoRefreshToggle
                enabled={autoRefresh}
                onEnabledChange={setAutoRefresh}
                onRefresh={refreshTable}
                storageKey={AUTO_REFRESH_STORAGE_KEY}
              />
            </div>
            <DataTablePageSize value={pageSize} onChange={setPageSize} options={lengthMenu} />
          </div>

          <p className="text-sm text-gray-600 mb-3">
            Use the toolbar for <strong>search</strong> and <strong>Columns</strong>. Sort via headers where
            available. Open an order for quotes, approvals, and pricing.
          </p>

          <AdminServerList
            ref={tableRef}
            fetchPage={fetchPage}
            columnDefs={orderedColumnDefs}
            visibility={visibility}
            renderActions={renderActions}
            onColumnReorder={onColumnReorder}
            search={debouncedSearch}
            showSearchInput={false}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            lengthMenu={lengthMenu}
            defaultSortKey="created_at"
            defaultSortDir="desc"
            onCountsChange={handleCounts}
            getRowClassName={getRowClassName}
            emptyText="No freight orders yet."
          />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreightOrdersPage;
